import React, { useEffect, useRef, useState } from "react";
import * as paths from "../../../paths/index";
import { Link } from "gatsby";
import { HiChevronDown } from "react-icons/hi";
import { useBreakpointMdUp } from "../../../hooks/useBreakpoint";
import { Nav, StyledNavItem, StyledSubNav } from "./Navigation.styles";
import { trackGa, GaEventNames } from "../../../helpers/track";
import { isLinkSameDomain } from "../../../helpers/isLinkSameDomain";

enum NavMenus {
  individuals = "Individuals",
  universe = "Universe",
  insights = "Insights",
  about = "About",
}

interface NavItemProps {
  label: NavMenus;
}

interface SubNavItemProps {
  label: string;
  url: string;
  isNew?: boolean;
  isSoon?: boolean;
  active: boolean;
}

interface SubNavProps {
  open: boolean;
  items: SubNavItemProps[];
  navParentLabel: NavMenus;
}

function NavItem({ label }: NavItemProps) {
  return (
    <StyledNavItem>
      {label} <HiChevronDown />
    </StyledNavItem>
  );
}

function SubNav({ open = false, items, navParentLabel }: SubNavProps) {
  const subNavRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0");

  useEffect(() => {
    setMaxHeight(open ? subNavRef.current!["scrollHeight"] + "px" : "0");
  }, [open]);

  return (
    <StyledSubNav
      ref={subNavRef}
      className={open ? "open" : ""}
      style={{ maxHeight: maxHeight }}
    >
      {items.map((navItem) => {
        const navItemClass = (navItem: SubNavItemProps) => {
          let classes = "";
          if (navItem.isNew) classes = "new";
          if (navItem.isSoon) classes += " soon";
          return classes;
        };
        const isGatsbyLink = isLinkSameDomain(navItem.url);
        return (
          navItem.active && (
            <li
              key={navItem.label}
              onClick={(e) => {
                trackGa({
                  event: GaEventNames.navigationClick,
                  navParent: navParentLabel,
                  link_url: navItem.url,
                  link_text: navItem.label,
                });
              }}
            >
              {isGatsbyLink ? (
                <Link className={navItemClass(navItem)} to={navItem.url}>
                  {navItem.label}
                </Link>
              ) : (
                <a className={navItemClass(navItem)} href={navItem.url}>
                  {navItem.label}
                </a>
              )}
            </li>
          )
        );
      })}
    </StyledSubNav>
  );
}

interface MenuProps {
  label: NavMenus;
  menu: SubNavItemProps[];
  open: boolean;
  onEnter: (menu: NavMenus) => void;
  onClick: (menu: NavMenus) => void;
  onLeave: () => void;
}

function Menu({ label, menu, open, onEnter, onClick, onLeave }: MenuProps) {
  return (
    <li
      onMouseEnter={() => onEnter(label)}
      onMouseLeave={onLeave}
      onClick={() => onClick(label)}
    >
      <NavItem label={label} />
      <SubNav navParentLabel={label} items={menu} open={open} />
    </li>
  );
}

export interface NavigationProps {
  extraClass?: string;
}

export function Navigation({ extraClass, ...props }: NavigationProps) {
  return (
    <Nav className={extraClass || ""} {...props}>
      <li>
        <Link to={paths.tillitClosingFaqsPath}>FAQs</Link>
      </li>
      <li>
        <Link to={paths.tillitContactPath}>Contact us</Link>
      </li>
    </Nav>
  );
}

export type CustomNavProps = {
  label: string;
  url: string;
  target?: "_self" | "_blank";
};

export interface CustomNavigationProps {
  extraClass?: string;
  nav: CustomNavProps[];
}

export function CustomNavigation({
  extraClass,
  nav,
  ...props
}: CustomNavigationProps) {
  return (
    <Nav className={extraClass || ""} {...props}>
      {nav.map((item, i: number) => {
        // the gatsby Link component cannot be used in this instance,
        // Neither <Link> nor navigate can be used for in-route navigation
        // with a hash or query parameter. If you need this behavior, you should
        // either use an anchor tag or import the @reach/router package—which Gatsby
        // already depends upon—to make use of its navigate function
        // source: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-link/#recommendations-for-programmatic-in-app-navigation
        return (
          <li key={item.label + "-" + i}>
            <a href={item.url} target={item.target || "_blank"}>
              {item.label}
            </a>
          </li>
        );
      })}
    </Nav>
  );
}
